import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Avatar,
  Divider,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  useColorModeValue,
  useToast,
  Input,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdArrowForward,
  MdAccessTime,
  MdLocationOn,
  MdInfo,
  MdPhone,
  MdEmail,
  MdCalendarToday,
  MdPerson,
  MdLocalHospital,
  MdDirectionsCar,
  MdAttachMoney,
  MdMoreVert,
  MdReceipt,
  MdPrint,
  MdTimer,
  MdDownload,
  MdAttachFile,
  MdLocationCity,
  MdMedicalServices
} from "react-icons/md";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getMission, getUsers, getAllPatients, getVehicules, getPatient } from "../../../api/api";
import GoogleMapsLoader, { useGoogleMaps } from "components/GoogleMapsLoader";
import axios from "axios";
import { io } from "socket.io-client";
import config, { BASE_URL } from "config/api";
import LoadingSpinner from "components/LoadingSpinner";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import teleconsultationImg from "assets/img/Téléconsultation.png";
import consultationDomicileImg from "assets/img/Consultation à domicile.png";
import acteInfirmierImg from "assets/img/Acte infirmier.png";
import ambulanceImg from "assets/img/ambulance.png";
import chuTempBg from "assets/img/chu_temp.jpg";

dayjs.extend(utc);
dayjs.extend(timezone);

const getSocketServerUrl = () => {
  try {
    return new URL(BASE_URL).origin;
  } catch (_) {
    return BASE_URL;
  }
};

const MissionDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { loaded } = useGoogleMaps();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [nurse, setNurse] = useState(null);
  const [ambulancier, setAmbulancier] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [travelTime, setTravelTime] = useState(null);
  const [activeTab, setActiveTab] = useState("apercu");
  const [callHistory, setCallHistory] = useState([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const [callHistoryError, setCallHistoryError] = useState(null);
  const [hasLoadedCallHistory, setHasLoadedCallHistory] = useState(false);
  const [isBonTransportModalOpen, setIsBonTransportModalOpen] = useState(false);
  const [bonTransportMotif, setBonTransportMotif] = useState("");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Helper functions
  const getPreferredId = (...values) => {
    for (const value of values) {
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return null;
  };

  const findUserById = (id) => {
    return users.find(u => u.id == id);
  };

  const parseLocationString = (locString) => {
    if (!locString) return null;
    const parts = locString.split(',');
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  };

  const fetchVehicleLocation = async (trackerId) => {
    try {
      const data = {
        // Utiliser le hash depuis la config (variable d'environnement)
        hash: config.navixy.hash,
        tracker_id: trackerId
      };
      const response = await axios.post(
        `${config.navixy.apiUrl}/tracker/get_state`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
          // Ne pas envoyer de cookies/credentials vers Navixy pour éviter les erreurs CORS
          withCredentials: false,
        }
      );
      return {
        lat: response.data.state.gps.location.lat,
        lng: response.data.state.gps.location.lng
      };
    } catch (error) {
      console.error('Error fetching vehicle location:', error);
      return null;
    }
  };

  const calculateTravelTime = async () => {
    if (!mission) return;

    try {
      const missionType = (mission.type_mission || '').toLowerCase();
      let originCoords = null;

      if (missionType === 'ambulance') {
        const vehicle = vehicles.find(v => v.user_id === mission.ambulancier_id);
        if (!vehicle) {
          toast({ title: 'Véhicule non trouvé', status: 'error', isClosable: true });
          return;
        }

        if (!vehicle.tracker_id) {
          toast({ title: 'Aucun tracker disponible', status: 'error', isClosable: true });
          return;
        }

        const vehicleLocation = await fetchVehicleLocation(vehicle.tracker_id);
        if (!vehicleLocation || !vehicleLocation.lat || !vehicleLocation.lng) {
          toast({ title: 'Localisation du véhicule non disponible', status: 'error', isClosable: true });
          return;
        }
        originCoords = vehicleLocation;
      } else if (missionType === 'acte infirmier') {
        const nurseId = getPreferredId(mission.nurse_id, mission.nurse, mission.nurseId);
        const nurseData = findUserById(nurseId) || users.find(u => u.rôle === 'infirmier' || u.rôle === 'infirmière' && u.id == nurseId);
        const nurseLoc = nurseData?.loc || nurseData?.location;
        originCoords = parseLocationString(nurseLoc);
        if (!originCoords) {
          toast({ title: "Localisation de l'infirmier indisponible", status: 'error', isClosable: true });
          return;
        }
      } else if (missionType === 'consultation à domicile' || missionType === 'consultation a domicile') {
        const doctorId = getPreferredId(mission.doctor_id, mission.doctor, mission.doctorId);
        const doctorData = findUserById(doctorId) || users.find(u => u.rôle === 'médecin' && u.id == doctorId);
        const doctorLoc = doctorData?.loc || doctorData?.location;
        originCoords = parseLocationString(doctorLoc);
        if (!originCoords) {
          toast({ title: "Localisation du médecin indisponible", status: 'error', isClosable: true });
          return;
        }
      } else {
        toast({ title: 'Type de mission non pris en charge pour ce calcul', status: 'warning', isClosable: true });
        return;
      }

      // Vérifier que Google Maps est chargé
      if (!window.google || !window.google.maps) {
        toast({ title: 'Google Maps non disponible', status: 'error', isClosable: true });
        return;
      }

      // Utiliser Google Maps Directions Service
      const directionsService = new window.google.maps.DirectionsService();

      const request = {
        origin: new window.google.maps.LatLng(originCoords.lat, originCoords.lng),
        destination: mission.adresse,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
        avoidHighways: false,
        avoidTolls: false
      };

      directionsService.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const route = result.routes[0];
          const leg = route.legs[0];

          const distance = leg.distance.text;
          const duration = leg.duration.text;

          // Stocker le résultat dans l'état
          setTravelTime({
            distance: distance,
            duration: duration,
            missionId: id
          });

          // Afficher le résultat dans un toast
          toast({
            title: `Temps de trajet calculé`,
            description: `Distance: ${distance} - Temps: ${duration}`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Impossible de calculer le trajet',
            description: `Erreur: ${status}`,
            status: 'error',
            isClosable: true
          });
        }
      });

    } catch (error) {
      console.error('Erreur lors du calcul du temps:', error);
      toast({ title: 'Erreur lors du calcul du temps de trajet', status: 'error', isClosable: true });
    }
  };



  const hasTravelOrigin = () => {
    if (!mission) return false;
    const missionType = (mission.type_mission || '').toLowerCase();
    if (missionType === 'ambulance') {
      const vehicle = vehicles.find(v => v.user_id === mission.ambulancier_id);
      return !!vehicle?.tracker_id;
    } else if (missionType === 'acte infirmier') {
      const nurseId = getPreferredId(mission.nurse_id, mission.nurse, mission.nurseId);
      const nurseData = findUserById(nurseId) || users.find(u => (u.rôle === 'infirmier' || u.rôle === 'infirmière') && u.id == nurseId);
      return !!parseLocationString(nurseData?.loc || nurseData?.location);
    } else if (missionType === 'consultation à domicile' || missionType === 'consultation a domicile') {
      const doctorId = getPreferredId(mission.doctor_id, mission.doctor, mission.doctorId);
      const doctorData = findUserById(doctorId) || users.find(u => u.rôle === 'médecin' && u.id == doctorId);
      return !!parseLocationString(doctorData?.loc || doctorData?.location);
    }
    return false;
  };

  const handleFileUpload = async (event, missionId) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({ title: 'Veuillez sélectionner un fichier PDF', status: 'error', isClosable: true });
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'Le fichier ne doit pas dépasser 50MB', status: 'error', isClosable: true });
      return;
    }

    try {
      // Convertir le fichier en base64 pour votre API
      const reader = new FileReader();
      reader.onload = async function (e) {
        const base64Data = e.target.result;

        try {
          const response = await axios.put(
            `${BASE_URL}/missions/${missionId}/piece-jointe`,
            { piece_jointe: base64Data },
            {
              withCredentials: true,
              // Augmenter les timeouts pour les gros fichiers
              timeout: 60000, // 60 secondes
              maxContentLength: 50 * 1024 * 1024, // 50MB
              maxBodyLength: 50 * 1024 * 1024, // 50MB
            }
          );

          if (response.status === 200) {
            toast({ title: 'Pièce jointe uploadée avec succès', status: 'success', isClosable: true });

            // Refresh mission data
            const missionRes = await getMission(id);
            const missionData = missionRes.data || missionRes;
            setMission(missionData);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          if (error.response?.status === 413) {
            toast({ title: 'Fichier trop volumineux (max 50MB)', status: 'error', isClosable: true });
          } else if (error.response?.status === 400) {
            toast({ title: error.response.data.error || 'Format de fichier invalide', status: 'error', isClosable: true });
          } else {
            toast({ title: 'Erreur lors de l\'upload de la pièce jointe', status: 'error', isClosable: true });
          }
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast({ title: 'Erreur lors de la lecture du fichier', status: 'error', isClosable: true });
    }
  };

  const getPieceJointeFileName = (missionId) => {
    if (patient) {
      const base = `${patient.nom || ""} ${patient.prenom || ""}`.trim();
      if (base) {
        const safe = base
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[<>:"/\\|?*\x00-\x1f]+/g, "-")
          .replace(/\s+/g, "_")
          .replace(/_+/g, "_")
          .replace(/^[_-]+|[_-]+$/g, "")
          .slice(0, 120);
        if (safe) return `${safe}.pdf`;
      }
    }
    return `mission-${missionId}.pdf`;
  };

  const handleFileDownload = async (missionId) => {
    try {
      // Utiliser la route dédiée pour le téléchargement
      const response = await axios.get(
        `${BASE_URL}/missions/${missionId}/piece-jointe/download`,
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );

      // Créer un blob et le télécharger
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getPieceJointeFileName(missionId);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({ title: 'Téléchargement réussi', status: 'success', isClosable: true });
    } catch (error) {
      console.error('Error downloading file:', error);
      if (error.response?.status === 404) {
        toast({ title: 'Aucune pièce jointe disponible', status: 'error', isClosable: true });
      } else {
        toast({ title: 'Erreur lors du téléchargement', status: 'error', isClosable: true });
      }
    }
  };

  useEffect(() => {
    const fetchMissionDetails = async () => {
      try {
        const missionRes = await getMission(id);
        const missionData = missionRes.data || missionRes;
        setMission(missionData);

        // Fetch all users to filter doctors and nurses
        const usersRes = await getUsers();
        const usersList = usersRes.data || [];
        setUsers(usersList);

        // Fetch vehicles
        const vehiclesRes = await getVehicules();
        const vehiclesList = vehiclesRes.data || [];
        setVehicles(vehiclesList);

        // Fetch related data
        // Fetch patient details directly by ID for better data completeness and performance
        if (missionData.patient_id) {
          try {
            const patientRes = await getPatient(missionData.patient_id);
            setPatient(patientRes.data || patientRes);
          } catch (patientError) {
            console.error("Error fetching patient details:", patientError);
            // Fallback to mission-embedded patient data if available
            if (missionData.patient) {
              setPatient(missionData.patient);
            }
          }
        }

        let doctorData = null;
        // Essayer de récupérer le docteur depuis la mission
        if (missionData.doctor_id) {
          const doctorsList = usersList.filter(user => user.rôle === 'médecin');
          doctorData = doctorsList.find(d => d.id == missionData.doctor_id);
          if (doctorData) {
            setDoctor(doctorData);

          } else {

          }
        }

        // Si le docteur n'est pas trouvé directement, essayer de le récupérer depuis l'ambulance
        if (!doctorData && missionData.ambulance_id) {
          const vehicle = vehiclesList.find(v => v.id == missionData.ambulance_id);
          if (vehicle) {
            console.log('Véhicule trouvé:', vehicle);
            if (vehicle.doctor_id) {
              const doctorsList = usersList.filter(user => user.rôle === 'médecin');
              const doctorFromVehicle = doctorsList.find(d => d.id == vehicle.doctor_id);
              if (doctorFromVehicle) {
                setDoctor(doctorFromVehicle);
                console.log('Docteur trouvé depuis véhicule:', doctorFromVehicle);
              } else {
                console.warn('Docteur non trouvé avec ID du véhicule:', vehicle.doctor_id);
              }
            } else {
              console.log('Aucun doctor_id dans le véhicule');
            }
          } else {
            console.warn('Véhicule non trouvé avec ID:', missionData.ambulance_id);
          }
        }

        if (missionData.nurse_id) {
          const nursesList = usersList.filter(user => user.rôle === 'infirmier' || user.rôle === 'infirmière');
          const nurseData = nursesList.find(n => n.id == missionData.nurse_id);
          setNurse(nurseData);
        }

        if (missionData.ambulancier_id) {
          const ambulancierData = usersList.find(u => u.id == missionData.ambulancier_id);
          setAmbulancier(ambulancierData);
        }
      } catch (error) {
        console.error("Error fetching mission details:", error);
        toast({
          title: "Erreur lors du chargement",
          description: "Impossible de charger les détails de la mission",
          status: "error",
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMissionDetails();
    }
  }, [id]);

  // Refetch régulier (toutes les 3s) pour un affichage "quasi temps réel"
  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    let isFetching = false;

    const refetchMission = async () => {
      if (isFetching) return;
      isFetching = true;
      try {
        const missionRes = await getMission(id);
        const missionData = missionRes.data || missionRes;
        if (!isMounted) return;
        setMission(missionData);
      } catch (error) {
        // silencieux pour éviter les toasts toutes les 3s en cas d'erreur réseau
        console.error("Error polling mission details:", error);
      } finally {
        isFetching = false;
      }
    };

    // petit "kick" immédiat + intervalle
    refetchMission();
    const intervalId = setInterval(refetchMission, 3000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const missionId = String(id);
    const socket = io(getSocketServerUrl(), {
      withCredentials: true,
      transports: ['websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    const refreshMission = async () => {
      try {
        const missionRes = await getMission(missionId);
        const missionData = missionRes.data || missionRes;
        setMission(missionData);
      } catch (error) {
        console.error('Error refreshing mission details via socket:', error);
      }
    };

    socket.on('connect', () => {
      socket.emit('join_mission', { missionId });
    });

    const onMissionUpdated = (payload) => {
      if (!payload?.id) return;
      if (String(payload.id) !== missionId) return;
      refreshMission();
    };

    const onMissionUpload = (payload) => {
      if (!payload?.missionId) return;
      if (String(payload.missionId) !== missionId) return;
      refreshMission();
    };

    socket.on('mission:updated', onMissionUpdated);
    socket.on('mission:upload', onMissionUpload);

    return () => {
      socket.off('mission:updated', onMissionUpdated);
      socket.off('mission:upload', onMissionUpload);
      socket.emit('leave_mission', { missionId });
      socket.disconnect();
    };
  }, [id]);

  // Charger l'historique d'appels de la mission (GET /missioncalls/mission/:missionId)
  useEffect(() => {
    const loadCallHistory = async () => {
      if (!id) {
        console.warn('[Historique appels] Pas de mission id, annulation du chargement');
        return;
      }
      try {
        console.log('[Historique appels] Chargement pour mission id =', id);
        setCallHistoryLoading(true);
        setCallHistoryError(null);

        const response = await axios.get(`${BASE_URL}/missioncalls/mission/${id}`, {
          withCredentials: true, // envoyé automatiquement les cookies (auth_token)
        });

        const data = response.data;
        console.log('[Historique appels] Réponse brute API =', data);
        const calls = Array.isArray(data) ? data : (data?.data || []);
        console.log('[Historique appels] Nombre d\'enregistrements =', calls.length);
        setCallHistory(calls);
      } catch (error) {
        console.error('Erreur chargement historique appels:', error);
        setCallHistoryError("Impossible de charger l'historique des appels");
      } finally {
        setCallHistoryLoading(false);
        setHasLoadedCallHistory(true);
      }
    };

    if (activeTab === "historique" && !hasLoadedCallHistory && !callHistoryLoading) {
      console.log('[Historique appels] Onglet historique actif, déclenchement du chargement');
      loadCallHistory();
    }
  }, [id, activeTab, hasLoadedCallHistory, callHistoryLoading]);

  if (loading) {
    return <LoadingSpinner message="Chargement des détails de la mission..." size="lg" fullHeight />;
  }

  if (!mission) {
    return <Box p="20px">Mission non trouvée</Box>;
  }

  // Timeline data - utiliser les données réelles de la mission
  const getTimelineSteps = () => {
    if (!mission) return [];

    const steps = [];

    if (mission.heure_affectation) {
      steps.push({
        label: "Affectation",
        time: dayjs(mission.heure_affectation).format("HH:mm"),
        duration: null
      });
    }

    if (mission.heure_depart) {
      const departureTime = dayjs(mission.heure_depart);
      // Utiliser temps_depart si disponible, sinon calculer depuis heure_affectation
      let durationStr = null;
      if (mission.temps_depart) {
        // temps_depart est au format "HH:mm:ss" ou "HH:mm"
        const timeParts = mission.temps_depart.split(':');
        if (timeParts.length >= 2) {
          // Nettoyer les secondes en supprimant les microsecondes après le point
          const seconds = timeParts[2] || '00';
          const cleanSeconds = seconds.split('.')[0];
          durationStr = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
        }
      } else {
        const affectationTime = mission.heure_affectation ? dayjs(mission.heure_affectation) : null;
        const duration = affectationTime ? departureTime.diff(affectationTime, 'second') : null;
        durationStr = duration ? `${Math.floor(duration / 3600).toString().padStart(2, '0')}:${Math.floor((duration % 3600) / 60).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}` : null;
      }

      steps.push({
        label: "Départ",
        time: departureTime.format("HH:mm"),
        duration: durationStr
      });
    }

    if (mission.heure_arrivee) {
      const arrivalTime = dayjs(mission.heure_arrivee);
      // Utiliser temps_arrivee si disponible, sinon calculer depuis heure_depart
      let durationStr = null;
      if (mission.temps_arrivee) {
        const timeParts = mission.temps_arrivee.split(':');
        if (timeParts.length >= 2) {
          // Nettoyer les secondes en supprimant les microsecondes après le point
          const seconds = timeParts[2] || '00';
          const cleanSeconds = seconds.split('.')[0];
          durationStr = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
        }
      } else {
        const departureTime = mission.heure_depart ? dayjs(mission.heure_depart) : null;
        const duration = departureTime ? arrivalTime.diff(departureTime, 'second') : null;
        durationStr = duration ? `${Math.floor(duration / 3600).toString().padStart(2, '0')}:${Math.floor((duration % 3600) / 60).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}` : null;
      }

      steps.push({
        label: "d'Arrivée",
        time: arrivalTime.format("HH:mm"),
        duration: durationStr
      });
    }

    if (mission.heure_depart_patient || mission.heure_redepart) {
      const departurePatientTime = dayjs(mission.heure_depart_patient || mission.heure_redepart);
      // Utiliser temps_redepart si disponible, sinon calculer depuis heure_arrivee
      let durationStr = null;
      if (mission.temps_redepart) {
        const timeParts = mission.temps_redepart.split(':');
        if (timeParts.length >= 2) {
          // Nettoyer les secondes en supprimant les microsecondes après le point
          const seconds = timeParts[2] || '00';
          const cleanSeconds = seconds.split('.')[0];
          durationStr = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
        }
      } else {
        const arrivalTime = mission.heure_arrivee ? dayjs(mission.heure_arrivee) : null;
        const duration = arrivalTime ? departurePatientTime.diff(arrivalTime, 'second') : null;
        durationStr = duration ? `${Math.floor(duration / 3600).toString().padStart(2, '0')}:${Math.floor((duration % 3600) / 60).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}` : null;
      }

      steps.push({
        label: "Départ avec patient",
        time: departurePatientTime.format("HH:mm"),
        duration: durationStr
      });
    }

    if (mission.heure_fin) {
      const endTime = dayjs(mission.heure_fin);
      // Utiliser temps_fin si disponible, sinon calculer depuis heure_depart_patient
      let durationStr = null;
      if (mission.temps_fin) {
        const timeParts = mission.temps_fin.split(':');
        if (timeParts.length >= 2) {
          // Nettoyer les secondes en supprimant les microsecondes après le point
          const seconds = timeParts[2] || '00';
          const cleanSeconds = seconds.split('.')[0];
          durationStr = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
        }
      } else {
        const departurePatientTime = mission.heure_depart_patient || mission.heure_redepart ? dayjs(mission.heure_depart_patient || mission.heure_redepart) : null;
        const duration = departurePatientTime ? endTime.diff(departurePatientTime, 'second') : null;
        durationStr = duration ? `${Math.floor(duration / 3600).toString().padStart(2, '0')}:${Math.floor((duration % 3600) / 60).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}` : null;
      }

      steps.push({
        label: "Fin",
        time: endTime.format("HH:mm"),
        duration: durationStr
      });
    }

    return steps;
  };

  const formatSecondsToTime = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const totalSeconds = parseInt(seconds, 10);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTotalTime = () => {
    // Utiliser temps_total si disponible
    if (mission?.temps_total) {
      const timeParts = mission.temps_total.split(':');
      if (timeParts.length >= 2) {
        // Nettoyer les secondes en supprimant les microsecondes après le point
        const seconds = timeParts[2] || '00';
        const cleanSeconds = seconds.split('.')[0]; // Prendre seulement la partie avant le point
        return `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
      }
    }

    // Sinon calculer depuis les heures
    if (!mission || !mission.heure_affectation || !mission.heure_fin) {
      return "00:05:26";
    }

    const start = dayjs(mission.heure_affectation);
    const end = dayjs(mission.heure_fin);
    const totalSeconds = end.diff(start, 'second');
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (statut) => {
    switch (statut?.toLowerCase()) {
      case "urgente":
        return "#F8285A";
      case "en cours":
        return "#459CE9";
      case "créée":
        return "#459CE9";
      case "terminée":
        return "#29BACD";
      default:
        return "#656C7A";
    }
  };

  const getStatusBg = (statut) => {
    switch (statut?.toLowerCase()) {
      case "urgente":
        return "#F8285A";
      case "en cours":
        return "#459CE9";
      case "créée":
        return "#459CE9";
      case "terminée":
        return "#29BACD";
      default:
        return "#656C7A";
    }
  };

  // Fonction pour obtenir les couleurs selon l'étape de la timeline
  const getStepColors = (label) => {
    const labelLower = label?.toLowerCase() || '';
    if (labelLower.includes('affectation')) {
      return {
        bg: "#E3F2FD",
        border: "rgba(69,156,233,0.2)",
        color: "#459CE9",
        iconColor: "#459CE9"
      };
    } else if (labelLower.includes('départ') && !labelLower.includes('patient')) {
      return {
        bg: "#E5E5E5",
        border: "rgba(255,255,255,0.2)",
        color: "#455879",
        iconColor: "#455879"
      };
    } else if (labelLower.includes('arrivée')) {
      return {
        bg: "#FFF8DD",
        border: "rgba(246,177,0,0.2)",
        color: "#F6B100",
        iconColor: "#F6B100"
      };
    } else if (labelLower.includes('départ') && labelLower.includes('patient')) {
      return {
        bg: "#E3F2FD",
        border: "rgba(69,156,233,0.2)",
        color: "#459CE9",
        iconColor: "#459CE9"
      };
    } else if (labelLower.includes('fin')) {
      return {
        bg: "#EAF8FA",
        border: "rgba(41,186,205,0.2)",
        color: "#29BACD",
        iconColor: "#29BACD"
      };
    }
    // Par défaut
    return {
      bg: "#F9F9F9",
      border: "#E5E5E5",
      color: "#455879",
      iconColor: "#455879"
    };
  };

  const openBonTransportModal = () => {
    if (!mission) return;
    setBonTransportMotif(mission.cause || "");
    setIsBonTransportModalOpen(true);
  };

  const generateBonTransportDocument = () => {
    if (!mission) {
      toast({ title: "Mission introuvable", status: "error", isClosable: true });
      return;
    }

    const dateMission = dayjs(mission.created_at || mission.date_mission).format("DD/MM/YYYY");
    const heureOrdre = dayjs().format("HH");
    const minuteOrdre = dayjs().format("mm");

    const dossierNumero = mission.id || "";
    const patientName = patient ? `${patient.nom || ""} ${patient.prenom || ""}`.trim() : "";

    const origineHopital = mission.adresse || "";
    const origineService = mission.service_a || "";

    const destinationHopital = mission.adresse_destination || "";
    const destinationService = mission.service_b || "";

    const typeAmbulance = (mission.type_ambulance || "").toUpperCase();
    const isMedicalise = typeAmbulance === "TAR" || typeAmbulance === "TAS";

    const creatorId = mission?.créé_par || mission?.cree_par || mission?.created_by;
    const creatorUser = creatorId ? users.find(u => u.id == creatorId) : null;
    const medRegulateur = creatorUser ? `${creatorUser.nom || ""} ${creatorUser.prenom || ""}`.trim() : "";

    const docAUser = mission.doc_a ? users.find(u => u.id == mission.doc_a) : null;
    const medDemandeur = docAUser ? `${docAUser.nom || ""} ${docAUser.prenom || ""}`.trim() : "";

    const docBUser = mission.doc_b ? users.find(u => u.id == mission.doc_b) : null;
    const medReceveur = docBUser ? `${docBUser.nom || ""} ${docBUser.prenom || ""}`.trim() : "";

    const motif = bonTransportMotif || mission.cause || "";

    const html = `<!DOCTYPE html>
<html lang="fr">
  <head>
    <meta charSet="utf-8" />
    <title>Bon de transport urgent</title>
    <style>
      * { box-sizing: border-box; }
      @page {
        size: A4;
        margin: 0;
      }
      html, body {
        width: 210mm;
        height: 297mm;
        margin: 0;
      }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 11pt;
      }
      table { border-collapse: collapse; }
      h1, h2, h3, h4, h5 { margin: 0; padding: 0; }

      .page {
        width: 210mm;
        height: 297mm;
        position: relative;
        overflow: hidden;
      }

      .bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 0;
      }

      .content {
        position: relative;
        z-index: 1;
        padding: 0 15mm;
        padding-top: 250px;
      }

      .header-table {
        width: 100%;
        margin-bottom: 6mm;
      }

      .header-left {
        font-size: 9pt;
        vertical-align: top;
      }

      .header-center {
        text-align: center;
        vertical-align: top;
      }

      .header-chu {
        font-size: 22pt;
        font-weight: bold;
      }

      .header-right {
        font-size: 9pt;
        vertical-align: top;
        text-align: right;
      }

      .title-main {
        font-size: 13pt;
        font-weight: bold;
        margin-top: 4mm;
        text-transform: uppercase;
      }

      .order-time {
        font-size: 10pt;
        margin-top: 2mm;
      }

      .block {
        border: 1px solid #000;
        padding: 3mm 4mm;
        margin-bottom: 3mm;
      }

      .row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 3mm;
      }

      .row > div {
        flex: 1;
      }

      .row > div:not(:last-child) {
        margin-right: 6mm;
      }

      .label {
        font-weight: bold;
        text-transform: uppercase;
        font-size: 10pt;
      }

      .value {
        border-bottom: 1px solid #000;
        min-height: 14px;
        padding-top: 1mm;
        font-size: 10pt;
      }

      .checkbox-row {
        margin-top: 2mm;
        font-size: 10pt;
      }

      .checkbox-item {
        display: inline-block;
        margin-right: 8mm;
      }

      .footer-sign {
        margin-top: 8mm;
        display: flex;
        justify-content: space-between;
        font-size: 10pt;
      }

      .footer-sign div {
        width: 32%;
        text-align: center;
      }

      .small-note {
        font-size: 8pt;
        margin-top: 8mm;
        text-align: justify;
      }
    </style>
  </head>
  <body>
    <div class="page">
      <img class="bg" src="${chuTempBg}" alt="" />
      <div class="content">


      <div style="text-align:center;">
        <div class="title-main">BON DE TRANSPORT URGENT</div>
        <div class="order-time">
         
        </div>
        <br/>
         <br/>
      </div>

      <div class="block">
        <div class="row">
          <div>
            <div class="label">DATE</div>
            <div class="value">${dateMission}</div>
          </div>
          <div>
            <div class="label">NUMERO DE DOSSIER</div>
            <div class="value">${dossierNumero}</div>
          </div>
        </div>
        <div style="margin-bottom:3mm;">
          <div class="label">NOM DU PATIENT</div>
          <div class="value">${patientName}</div>
        </div>
        <div>
          <div class="label">MOTIF DU TRANSPORT</div>
          <div class="value">${motif}</div>
        </div>
      </div>

      <div class="block">
        <div style="margin-bottom:3mm;">
          <div class="label">ORIGINE DE TRANSFER</div>
        </div>
        <div class="row">
          <div>
            <div class="label">Hôpital</div>
            <div class="value">${origineHopital}</div>
          </div>
          <div>
            <div class="label">Service</div>
            <div class="value">${origineService}</div>
          </div>
        </div>
        <div class="label" style="margin-top:2mm;">TYPE ET EQUIPE DE TRANSPORT :</div>
        <div class="checkbox-row">
          <span class="checkbox-item">[${!isMedicalise ? "X" : " "}] Non médicalisé</span>
          <span class="checkbox-item">[${isMedicalise ? "X" : " "}] Médicalisé</span>
          <span class="checkbox-item">[ ] Accompagnant</span>
          <span class="checkbox-item">[ ] Para médicalisé</span>
        </div>
      </div>

      <div class="block">
        <div style="margin-bottom:3mm;">
          <div class="label">DESTINATION</div>
        </div>
        <div class="row">
          <div>
            <div class="label">Hôpital</div>
            <div class="value">${destinationHopital}</div>
          </div>
          <div>
            <div class="label">Service</div>
            <div class="value">${destinationService}</div>
          </div>
        </div>
      </div>

      <div class="footer-sign">
        <div>
          <div class="label">Nom du Régulateur</div>
          <div class="value">${medRegulateur}</div>
        </div>
        <div>
          <div class="label">Nom du médecin<br/>demandeur du transfert</div>
          <div class="value">${medDemandeur}</div>
        </div>
        <div>
          <div class="label">Nom du médecin<br/>receveur du transfert</div>
          <div class="value">${medReceveur}</div>
        </div>
      </div>

      
      </div>
    </div>
  </body>
</html>`;

    const win = window.open("", "_blank");
    if (!win) {
      toast({ title: "Popup bloquée", description: "Veuillez autoriser les popups pour générer le document.", status: "error", isClosable: true });
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();

    try {
      setTimeout(() => {
        win.print();
      }, 500);
    } catch (e) { }

    setIsBonTransportModalOpen(false);
  };

  return (
    <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
      {/* Header with Breadcrumb */}
      <Box mb="20px">
        <HStack spacing="10px" mb="20px" align="center">
          <IconButton
            icon={<Icon as={MdArrowBack} />}
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/missions')}
            aria-label="Retour"
            w="36px"
            h="36px"
            borderRadius="6px"
            _hover={{ bg: "gray.100" }}
          />
          <Text
            fontSize="14px"
            color="#656C7A"
            cursor="pointer"
            _hover={{ color: "#455879" }}
            onClick={() => navigate('/admin/missions')}
          >
            Gestion de mission
          </Text>
          <Icon as={MdArrowForward} w="12px" h="12px" color="#656C7A" transform="rotate(-90deg)" />
          <Text
            fontSize="14px"
            color="#656C7A"
            cursor="pointer"
            _hover={{ color: "#455879" }}
            onClick={() => navigate('/admin/missions')}
          >
            Missions
          </Text>
          <Icon as={MdArrowForward} w="12px" h="12px" color="#656C7A" transform="rotate(-90deg)" />
          <Text fontSize="14px" color="#455879" fontWeight="600">
            Détails de la mission
          </Text>
        </HStack>

        {/* Mission Header Card */}
        <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
          <Flex justify="space-between" align="flex-start" mb="14px">
            <HStack spacing="20px">
              <Box w="72px" h="72px" borderRadius="6px" bg="#E3F2FD" display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={
                    mission.type_mission === "Téléconsultation" ? teleconsultationImg :
                      mission.type_mission === "Consultation à domicile" ? consultationDomicileImg :
                        mission.type_mission === "Acte infirmier" ? acteInfirmierImg :
                          ambulanceImg
                  }
                  alt={mission.type_mission}
                  w="50px"
                  h="50px"
                  objectFit="contain"
                />
              </Box>
              <VStack align="flex-start" spacing="4px">
                <Text fontSize="20px" fontWeight="600" color="#455879">
                  Réf. Mission {mission.bon_de_commande || `MTC-${mission.id}`} - {mission.type_mission}
                </Text>
                <HStack spacing="20px">
                  <HStack spacing="5px">
                    <Icon as={MdCalendarToday} w="16px" h="16px" color="#656C7A" />
                    <Text fontSize="14px" color="#656C7A">
                      Create Date : {dayjs(mission.created_at || mission.date_mission).format("DD/MM/YYYY HH:mm")}
                    </Text>
                  </HStack>
                  <HStack spacing="5px">
                    <Avatar size="sm" name={(() => {
                      const creatorId = mission?.créé_par || mission?.cree_par || mission?.created_by;
                      if (!creatorId) return "Admin";
                      const u = users.find(us => us.id == creatorId);
                      return u ? `${u.nom} ${u.prenom}` : "Admin";
                    })()} />
                    <Text fontSize="14px" color="#455879">
                      Créée par: {(() => {
                        const creatorId = mission?.créé_par || mission?.cree_par || mission?.created_by;
                        if (!creatorId) return "Admin";
                        const u = users.find(us => us.id == creatorId);
                        return u ? `${u.nom} ${u.prenom}` : String(creatorId);
                      })()}
                    </Text>
                  </HStack>
                  {mission.timer !== null && mission.timer !== undefined && (
                    <HStack spacing="5px">
                      <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                      <Text fontSize="14px" color="#455879">
                        Durée: {formatSecondsToTime(mission.timer) || "00:00:00"}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            </HStack>
            <HStack spacing="10px">
              <Button
                size="sm"
                variant="outline"
                borderColor={borderColor}
                onClick={calculateTravelTime}
                isDisabled={!hasTravelOrigin()}
                title="Calculer le temps de trajet de l'intervenant vers le patient"
              >
                {travelTime && travelTime.missionId === id ? (
                  <>⏱️ {travelTime.duration}</>
                ) : (
                  <>⏱️ Temps restant</>
                )}
              </Button>
              <Badge
                bg={getStatusBg(mission.statut)}
                color="white"
                borderRadius="20px"
                px="10px"
                py="6px"
                fontSize="12px"
                display="flex"
                alignItems="center"
                gap="6px"
              >
                <Box w="6px" h="6px" borderRadius="50%" bg="white" />
                {((mission.statut || "En cours") + "").toUpperCase()}
              </Badge>
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  icon={<Icon as={MdMoreVert} />}
                  variant="ghost"
                  size="sm"
                  aria-label="Actions"
                />
                <MenuList>
                  <MenuItem onClick={() => navigate(`/admin/missions/edit/${id}`)}>
                    Modifier
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
          <HStack spacing="0" borderTop="1px solid" borderColor={borderColor} pt="10px">
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "apercu" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("apercu")}
            >
              Aperçu de la mission
            </Button>
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "pieces" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("pieces")}
            >
              Pièces Jointes
              {mission.piece_jointe && (
                <Badge ml="5px" bg="#459CE9" color="white" borderRadius="10px" px="5px" py="1px" fontSize="10px">
                  01
                </Badge>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "historique" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("historique")}
            >
              Historique d'appels
            </Button>
          </HStack>
        </Box>
      </Box>

      {activeTab === "apercu" && (
        <Flex gap="20px" align="flex-start">
          {/* Left Column */}
          <Box flex="1" minW="960px">
            {/* Suivi de Mission */}
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">
                Suivi de Mission
              </Text>
              <Text fontSize="14px" color="#656C7A" mb="20px">
                Historique chronologique du déroulement de l'intervention
              </Text>

              {/* Timeline */}
              <VStack spacing="15px" align="stretch" mb="20px">
                {getTimelineSteps().length > 0 ? (
                  <>
                    <Box
                      bg="#F9F9F9"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="6px"
                      p="15px"
                    >
                      <Flex gap="15px" align="flex-start" flexWrap="nowrap" overflowX="auto">
                        {getTimelineSteps().map((step, index) => {
                          const steps = getTimelineSteps();
                          const stepColors = getStepColors(step.label);
                          const nextStepColors = index < steps.length - 1 ? getStepColors(steps[index + 1].label) : null;

                          return (
                            <React.Fragment key={index}>
                              <VStack spacing="10px" align="flex-start" minW="fit-content" flexShrink={0}>
                                <Text
                                  fontSize="14px"
                                  fontWeight="400"
                                  color="#656C7A"
                                  textTransform="uppercase"
                                >
                                  {step.label}
                                </Text>
                                <HStack
                                  spacing="6px"
                                  bg={stepColors.bg}
                                  border="1px solid"
                                  borderColor={stepColors.border}
                                  px="10px"
                                  py="8px"
                                  borderRadius="6px"
                                  justifyContent="center"
                                >
                                  <Icon
                                    as={MdAccessTime}
                                    w="14px"
                                    h="14px"
                                    color={stepColors.iconColor}
                                  />
                                  <Text
                                    fontSize="14px"
                                    color={stepColors.color}
                                    fontWeight="500"
                                  >
                                    {step.time}
                                  </Text>
                                </HStack>
                                {step.duration ? (
                                  <Box
                                    px="10px"
                                    py="8px"
                                    borderRadius="6px"
                                    w="fit-content"
                                  >
                                    <Text
                                      fontSize="14px"
                                      color="#455879"
                                      fontWeight="500"
                                    >
                                      {step.duration}
                                    </Text>
                                  </Box>
                                ) : (
                                  <Box
                                    px="10px"
                                    py="8px"
                                    borderRadius="6px"
                                    w="fit-content"
                                  >
                                    <Text fontSize="14px" color="#455879" fontWeight="500">
                                      Durée:
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                              {index < steps.length - 1 && (
                                <Box
                                  w="34px"
                                  h="92px"
                                  position="relative"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                  alignSelf="flex-start"
                                  mt="4px"
                                >
                                  <Box
                                    w="100%"
                                    h="2px"
                                    position="relative"
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Box
                                      w="100%"
                                      h="2px"
                                      borderTop="2px dashed"
                                      borderColor={nextStepColors?.color || "#E5E5E5"}
                                      style={{
                                        borderTopStyle: 'dashed',
                                        borderTopWidth: '2px',
                                        borderTopColor: nextStepColors?.color || "#E5E5E5"
                                      }}
                                    />
                                    <Box
                                      position="absolute"
                                      right="-4px"
                                      top="-4px"
                                      w="10px"
                                      h="10px"
                                      borderRadius="50%"
                                      bg={nextStepColors?.color || "#E5E5E5"}
                                      border="2px solid white"
                                      zIndex={1}
                                    />
                                  </Box>
                                </Box>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Flex>
                    </Box>
                    <HStack
                      spacing="6px"
                      bg="#E3F2FD"
                      px="10px"
                      py="8px"
                      borderRadius="6px"
                      w="100%"
                      border="1px solid"
                      borderColor="rgba(69,156,233,0.2)"
                    >
                      <Icon as={MdTimer} w="16px" h="16px" color="#459CE9" />
                      <Text fontSize="14px" color="#455879" fontWeight="500">
                        Temps total de l'intervention : <Text as="span" fontWeight="700" color="#455879">{getTotalTime()}</Text>
                      </Text>
                    </HStack>
                  </>
                ) : (
                  <Box
                    bg="#F9F9F9"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="6px"
                    p="40px"
                    textAlign="center"
                  >
                    <Text fontSize="14px" color="#656C7A">
                      Aucune donnée de suivi disponible. La mission n'a pas encore été effectuée.
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Informations de la Mission - Refonte */}
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Informations de la Mission
              </Text>

              <VStack spacing="25px" align="stretch">
                {/* Section 1: Détails de l'intervention */}
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Détails de l'intervention
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocalHospital} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Type de Mission</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879" fontWeight="600">
                        {mission.type_mission || "Non spécifié"}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Niveau d'urgence</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.cause || "Non spécifié"}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Urgence / État</Text>
                      </HStack>
                      <HStack spacing="10px">
                        <Badge
                          bg={mission.urgence ? "#F8285A" : "gray.400"}
                          color="white"
                          borderRadius="4px"
                          px="8px"
                          fontSize="10px"
                        >
                          {mission.urgence ? "URGENT" : "NORMAL"}
                        </Badge>
                        <Badge
                          bg={getStatusBg(mission.statut)}
                          color="white"
                          borderRadius="4px"
                          px="8px"
                          fontSize="10px"
                        >
                          {(mission.statut || "EN COURS").toUpperCase()}
                        </Badge>
                      </HStack>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Section 2: Origine / Hospitalisation A */}
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Point de départ / Origine
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocationOn} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Adresse / Hôpital</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879" fontWeight="500">
                        {mission.adresse || "Non spécifié"}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocalHospital} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Service / Médecin</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.service_a ? `Service: ${mission.service_a}` : ""}
                        {mission.doc_a ? (mission.service_a ? " | " : "") + "Médecin: " + (() => {
                          const med = users.find(u => u.id == mission.doc_a);
                          return med ? `${med.nom} ${med.prenom}` : mission.doc_a;
                        })() : (!mission.service_a ? "Non spécifié" : "")}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocationCity} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Ville</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.ville || "Non spécifié"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Section 3: Destination / Hospitalisation B */}
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Destination
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocationOn} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Adresse de destination</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879" fontWeight="500">
                        {mission.adresse_destination || "Non spécifié"}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocalHospital} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Service / Médecin</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.service_b ? `Service: ${mission.service_b}` : ""}
                        {mission.doc_b ? (mission.service_b ? " | " : "") + "Médecin: " + (() => {
                          const med = users.find(u => u.id == mission.doc_b);
                          return med ? `${med.nom} ${med.prenom}` : mission.doc_b;
                        })() : (!mission.service_b ? "Non spécifié" : "")}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdLocationCity} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Ville Destination</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.ville_dest || "Non spécifié"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Section 4: Logistique */}
                <Box pb="10px">
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Logistique & Matériel
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdDirectionsCar} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Ambulance assignée</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {(() => {
                          if (!mission.ambulance_id && !mission.type_ambulance) return "Non spécifié";
                          if (mission.ambulance_id) {
                            const vehicle = vehicles.find(v => v.id == mission.ambulance_id);
                            return vehicle ? `${vehicle.type_vehicule}${vehicle.ville ? ` - ${vehicle.ville}` : ''} (${vehicle.id})` : mission.type_ambulance || "Non spécifié";
                          }
                          return mission.type_ambulance || "Non spécifié";
                        })()}
                      </Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdMedicalServices} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Besoins en matériel</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">
                        {mission.demande_materiel || "Aucun matériel spécifique"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Section 5: Constantes et transport */}
                <Box pb="10px">
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Constantes et transport
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing="20px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">TA</Text>
                      <Text fontSize="14px" color="#455879">{mission.ta || "Non spécifié"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">FC</Text>
                      <Text fontSize="14px" color="#455879">{mission.fc || "Non spécifié"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">Glasgow</Text>
                      <Text fontSize="14px" color="#455879">{mission.glasgow || "Non spécifié"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">SPO2</Text>
                      <Text fontSize="14px" color="#455879">{mission.spo2 || "Non spécifié"}</Text>
                    </Box>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px" mt="20px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">Autre</Text>
                      <Text fontSize="14px" color="#455879">{mission.autre || "Non spécifié"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">Diagnostic principal</Text>
                      <Text fontSize="14px" color="#455879">{mission.diagnostic || "Non spécifié"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500" mb="5px">Motif du transport</Text>
                      <Text fontSize="14px" color="#455879">{mission.motif || "Non spécifié"}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </VStack>
            </Box>
          </Box>

          {/* Right Column */}
          <Box w="650px">
            {/* Patient Section - Améliorée */}
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Patient
              </Text>

              {patient ? (
                <VStack spacing="20px" align="stretch">
                  <HStack spacing="15px">
                    <Avatar size="lg" name={`${patient.nom} ${patient.prenom}`} bg="#006133" />
                    <VStack align="flex-start" spacing="2px" flex="1">
                      <Text fontSize="18px" fontWeight="700" color="#455879">
                        {patient.nom} {patient.prenom}
                      </Text>
                      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="10px">
                        {patient.sexe || "Non précisé"} {patient.age ? `• ${patient.age} ans` : ""}
                      </Badge>
                    </VStack>
                  </HStack>

                  <SimpleGrid columns={1} spacing="12px">
                    <HStack spacing="10px" bg="gray.50" px="12px" py="10px" borderRadius="8px">
                      <Icon as={MdPhone} w="18px" h="18px" color="#006133" />
                      <VStack align="flex-start" spacing="0">
                        <Text fontSize="11px" color="gray.500" fontWeight="bold">TÉLÉPHONE</Text>
                        <Text fontSize="14px" color="#455879" fontWeight="500">
                          {patient.téléphone || patient.telephone || "Non spécifié"}
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing="10px" bg="gray.50" px="12px" py="10px" borderRadius="8px">
                      <Icon as={MdEmail} w="18px" h="18px" color="#006133" />
                      <VStack align="flex-start" spacing="0">
                        <Text fontSize="11px" color="gray.500" fontWeight="bold">EMAIL</Text>
                        <Text fontSize="14px" color="#455879" fontWeight="500">
                          {patient.email || "Non spécifié"}
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing="10px" bg="gray.50" px="12px" py="10px" borderRadius="8px">
                      <Icon as={MdLocationOn} w="18px" h="18px" color="#006133" />
                      <VStack align="flex-start" spacing="0">
                        <Text fontSize="11px" color="gray.500" fontWeight="bold">ADRESSE & VILLE</Text>
                        <Text fontSize="14px" color="#455879" fontWeight="500">
                          {patient.adresse || "Adresse non spécifiée"}
                          {patient.ville ? ` • ${patient.ville}` : ""}
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing="10px" bg="gray.50" px="12px" py="10px" borderRadius="8px">
                      <Icon as={MdCalendarToday} w="18px" h="18px" color="#006133" />
                      <VStack align="flex-start" spacing="0">
                        <Text fontSize="11px" color="gray.500" fontWeight="bold">DATE DE NAISSANCE / CIN</Text>
                        <Text fontSize="14px" color="#455879" fontWeight="500">
                          {patient.date_naissance ? dayjs(patient.date_naissance).format("DD/MM/YYYY") : "Non spécifiée"}
                          {patient.cin ? ` • CIN: ${patient.cin}` : ""}
                        </Text>
                      </VStack>
                    </HStack>
                  </SimpleGrid>
                </VStack>
              ) : (
                <Flex direction="column" align="center" py="30px" bg="gray.50" borderRadius="8px">
                  <Icon as={MdPerson} w="40px" h="40px" color="gray.300" mb="10px" />
                  <Text fontSize="14px" color="gray.500" fontWeight="500">Aucune donnée patient disponible</Text>
                </Flex>
              )}
            </Box>



            {/* L'équipe assignée */}
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">
                L'équipe assignée
              </Text>

              <VStack spacing="15px" align="stretch">
                {doctor && (
                  <HStack spacing="10px">
                    <Avatar size="sm" name={`${doctor.nom} ${doctor.prenom}`} />
                    <VStack align="flex-start" spacing="5px" flex="1">
                      <Text fontSize="14px" fontWeight="600" color="#455879">
                        {doctor.nom} {doctor.prenom}
                      </Text>
                      <HStack spacing="10px">
                        <HStack spacing="5px" bg="gray.50" px="8px" py="6px" borderRadius="6px">
                          <Icon as={MdPhone} w="14px" h="14px" color="#656C7A" />
                          <Text fontSize="12px" color="#455879">
                            {doctor.téléphone || doctor.telephone || "Non spécifié"}
                          </Text>
                        </HStack>
                        <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                          médecin
                        </Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                )}

                {nurse && (
                  <HStack spacing="10px">
                    <Avatar size="sm" name={`${nurse.nom} ${nurse.prenom}`} />
                    <VStack align="flex-start" spacing="5px" flex="1">
                      <Text fontSize="14px" fontWeight="600" color="#455879">
                        {nurse.nom} {nurse.prenom}
                      </Text>
                      <HStack spacing="10px">
                        <HStack spacing="5px" bg="gray.50" px="8px" py="6px" borderRadius="6px">
                          <Icon as={MdPhone} w="14px" h="14px" color="#656C7A" />
                          <Text fontSize="12px" color="#455879">
                            {nurse.téléphone || nurse.telephone || "Non spécifié"}
                          </Text>
                        </HStack>
                        <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                          Infirmière
                        </Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                )}

                {ambulancier && (
                  <HStack spacing="10px">
                    <Avatar size="sm" name={`${ambulancier.nom} ${ambulancier.prenom}`} />
                    <VStack align="flex-start" spacing="5px" flex="1">
                      <Text fontSize="14px" fontWeight="600" color="#455879">
                        {ambulancier.nom} {ambulancier.prenom}
                      </Text>
                      <HStack spacing="10px">
                        <HStack spacing="5px" bg="gray.50" px="8px" py="6px" borderRadius="6px">
                          <Icon as={MdPhone} w="14px" h="14px" color="#656C7A" />
                          <Text fontSize="12px" color="#455879">
                            {ambulancier.téléphone || ambulancier.telephone || "Non spécifié"}
                          </Text>
                        </HStack>
                        <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                          Ambulancier
                        </Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                )}

                {!doctor && !nurse && !ambulancier && (
                  <Text fontSize="14px" color="#656C7A">Aucun membre d'équipe assigné</Text>
                )}
              </VStack>
            </Box>


          </Box>
        </Flex>
      )}

      {/* Pièces Jointes - Full Width when active */}
      {activeTab === "pieces" && (
        <Box w="100%" mt="20px">
          <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
            <Flex justify="space-between" align="center" mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879">
                Pièces Jointes
              </Text>
              <Button
                size="sm"
                leftIcon={<Icon as={MdPrint} />}
                variant="outline"
                onClick={openBonTransportModal}
              >
                Bon de transport urgent
              </Button>
            </Flex>

            <VStack spacing="20px" align="stretch">
              {mission.piece_jointe ? (
                <HStack
                  justify="space-between"
                  align="center"
                  flexWrap="wrap"
                  gap="12px"
                  p="14px"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor={borderColor}
                  bg="gray.50"
                >
                  <Box flex="1" minW="200px">
                    <Text fontWeight="500" color="#455879" mb="5px">
                      Fichier actuel
                    </Text>
                    <Text fontSize="14px" color="#656C7A">
                      {mission.piece_jointe === "exists" ||
                      mission.piece_jointe === "uploaded" ||
                      mission.piece_jointe
                        ? getPieceJointeFileName(mission.id)
                        : "Fichier PDF"}
                    </Text>
                  </Box>
                  <HStack spacing="10px" flexShrink={0}>
                    <IconButton
                      icon={<Icon as={MdDownload} />}
                      size="sm"
                      variant="outline"
                      borderColor={borderColor}
                      onClick={() => handleFileDownload(mission.id)}
                      aria-label="Télécharger"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={borderColor}
                      leftIcon={<Icon as={MdAttachFile} />}
                      onClick={() => document.getElementById("piece-jointe-upload").click()}
                    >
                      Remplacer
                    </Button>
                  </HStack>
                </HStack>
              ) : (
                <Text color="#656C7A" textAlign="center">
                  Aucune pièce jointe uploadée
                </Text>
              )}
              <Button
                leftIcon={<Icon as={MdAttachFile} />}
                variant="outline"
                borderColor={borderColor}
                onClick={() => document.getElementById("piece-jointe-upload").click()}
              >
                Ajouter une pièce jointe
              </Button>
            </VStack>

            {/* Hidden file input */}
            <Input
              id="piece-jointe-upload"
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFileUpload(e, mission.id)}
            />
          </Box>

          {isBonTransportModalOpen && (
            <Box
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(0,0,0,0.4)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={1400}
            >
              <Box
                bg={bgCard}
                borderRadius="8px"
                p="20px"
                border="1px solid"
                borderColor={borderColor}
                width="100%"
                maxW="500px"
              >
                <Text fontSize="16px" fontWeight="600" color="#455879" mb="10px">
                  Bon de transport urgent
                </Text>
                <Text fontSize="13px" color="#656C7A" mb="12px">
                  Saisissez le motif du transport. Les autres informations seront remplies automatiquement à partir de la mission.
                </Text>

                <Text fontSize="13px" fontWeight="500" color="#455879" mb="4px">
                  Motif du transport
                </Text>
                <Input
                  value={bonTransportMotif}
                  onChange={(e) => setBonTransportMotif(e.target.value)}
                  placeholder="Ex : Transfert vers CHU pour prise en charge spécialisée..."
                  mb="16px"
                />

                <HStack justify="flex-end" spacing="8px">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsBonTransportModalOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Icon as={MdPrint} />}
                    onClick={generateBonTransportDocument}
                  >
                    Générer le bon
                  </Button>
                </HStack>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Historique d'appels - Full Width when active */}
      {activeTab === "historique" && (
        <Box w="100%" mt="20px">
          <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
            <Text fontSize="16px" fontWeight="600" color="#455879" mb="10px">
              Historique d'appels
            </Text>
            <Text fontSize="13px" color="#656C7A" mb="16px">
              Enregistrements des appels liés à cette mission.
            </Text>

            {callHistoryLoading && (
              <LoadingSpinner message="Chargement de l'historique des appels..." size="sm" />
            )}

            {!callHistoryLoading && callHistoryError && (
              <Text fontSize="14px" color="red.500">
                {callHistoryError}
              </Text>
            )}

            {!callHistoryLoading && !callHistoryError && callHistory.length === 0 && (
              <Text fontSize="14px" color="#656C7A">
                Aucun appel dans l'historique.
              </Text>
            )}

            {!callHistoryLoading && !callHistoryError && callHistory.length > 0 && (
              <VStack spacing="10px" align="stretch" maxH="400px" overflowY="auto">
                {callHistory.map((call, index) => {
                  const callDate = call.created_at ? new Date(call.created_at) : null;
                  const dateStr = callDate
                    ? callDate.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : 'Date inconnue';
                  const audioUrl = call.audioUrl
                    ? (call.audioUrl.startsWith('http') ? call.audioUrl : `${BASE_URL}${call.audioUrl.startsWith('/') ? '' : '/'}${call.audioUrl}`)
                    : null;

                  return (
                    <Box
                      key={call.id || index}
                      borderRadius="6px"
                      border="1px solid"
                      borderColor={borderColor}
                      p="12px"
                      bg="gray.50"
                    >
                      <Flex align="center" justify="space-between" flexWrap="wrap" gap="10px">
                        <HStack spacing="10px">
                          <Text fontSize="20px">📞</Text>
                          <VStack spacing={1} align="flex-start">
                            <Text fontSize="14px" fontWeight="500" color="#455879">
                              Appel #{call.callid || call.id}
                            </Text>
                            <HStack spacing="6px">
                              <Icon as={MdAccessTime} w="14px" h="14px" color="#656C7A" />
                              <Text fontSize="12px" color="#656C7A">
                                {dateStr}
                              </Text>
                            </HStack>
                          </VStack>
                        </HStack>
                        {audioUrl && (
                          <Box flex="1" minW="200px" maxW="100%">
                            <audio
                              controls
                              src={audioUrl}
                              style={{ width: '100%', height: '36px', borderRadius: '6px' }}
                              preload="metadata"
                            >
                              Votre navigateur ne supporte pas la lecture audio.
                            </audio>
                          </Box>
                        )}
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const MissionDetails = () => {
  return (
    <GoogleMapsLoader>
      <MissionDetailsContent />
    </GoogleMapsLoader>
  );
};

export default MissionDetails;

